import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import CardActions from "@mui/material/CardActions";
import Collapse from "@mui/material/Collapse";
import {
  PolygonButton,
  RectangleButton,
  LineButton,
  MarkerButton,
  RoomSelectButton,
  GroupRoomSelectButton,
  MoreToolsButton,
  DeleteAllButton,
  ColorPicker,
  GridToggle,
} from "./DrawButtons";
import { actionMapDrawModeUpdated, actionMapCursorUpdated } from "../Map";
import {
  actionDrawIsDrawingUpdated,
  actionDrawResetMode,
  actionDrawUpdateClickCounter,
  actionDrawUpdateGroupFeature,
} from "./drawRedux";
import { actionDeleteAlert } from "../Alert";

export default function DrawTools({ featureGroups }) {
  const dispatch = useDispatch();
  const drawMode = useSelector((state) => state.mapReducer.drawMode);
  const clickCounter = useSelector((state) => state.drawReducer.clickCounter);

  const [moreToolsVisibile, setMoreToolsVisibile] = useState(false);
  const moreToolsClick = () => {
    setMoreToolsVisibile(!moreToolsVisibile);
  };

  const changeMode = (mode, resetFirst = true) => {
    changeDrawMode(mode, dispatch, drawMode, resetFirst);
  };

  const removeAll = () => {
    const [firstGroup] = featureGroups;
    if (!firstGroup) {
      return;
    }
    const targetIds = firstGroup.features.map((feature) => feature.id);
    dispatch(actionDrawUpdateGroupFeature("delete-all"));
    dispatch(
      actionMapDrawModeUpdated({
        mode: drawMode.mode,
        clearDrawing: true,
        data: targetIds,
      }),
    );
  };

  useEffect(() => {
    if (!drawMode.mode || drawMode.mode === "simple_select") {
      // remove instruction
      dispatch(actionDeleteAlert("draw"));
    }
  }, [dispatch, drawMode]);

  return (
    <>
      <PrimaryTools>
        <RectangleButton
          drawMode={drawMode}
          clickCounter={clickCounter}
          changeMode={changeMode}
        />
        <LineButton
          drawMode={drawMode}
          clickCounter={clickCounter}
          changeMode={changeMode}
        />
        <MarkerButton drawMode={drawMode} changeMode={changeMode} />
        <RoomSelectButton drawMode={drawMode} changeMode={changeMode} />
        <MoreToolsButton
          moreToolsVisibile={moreToolsVisibile}
          moreToolsClick={moreToolsClick}
        />
        <DeleteAllButton featureGroups={featureGroups} removeAll={removeAll} />
        <GridToggle />
        <ColorPicker />
      </PrimaryTools>
      <MoreTools moreToolsVisibile={moreToolsVisibile}>
        <PolygonButton
          drawMode={drawMode}
          clickCounter={clickCounter}
          changeMode={changeMode}
        />
        <GroupRoomSelectButton drawMode={drawMode} changeMode={changeMode} />
      </MoreTools>
    </>
  );
}

// containers
function PrimaryTools({ children }) {
  const zeroPadding = {
    paddingLeft: "0px",
    paddingRight: "0px",
    paddingTop: "0px",
  };

  return (
    <CardActions disableSpacing style={zeroPadding}>
      {children}
    </CardActions>
  );
}

function MoreTools({ moreToolsVisibile, children }) {
  const zeroPadding = {
    paddingLeft: "0px",
    paddingRight: "0px",
    paddingTop: "0px",
  };

  return (
    <Collapse in={moreToolsVisibile} timeout="auto">
      <CardActions disableSpacing style={zeroPadding}>
        {children}
      </CardActions>
    </Collapse>
  );
}

export function changeDrawMode(mode, dispatch, drawMode, resetFirst = true) {
  // user toggle off selected mode by clicking the same mode again or clicking cancel during drawing
  if (mode === drawMode.mode) {
    // cancel current mode and go back to simple_select
    mode = "simple_select";
  }

  if (resetFirst) {
    // Anytime we are asked to switch modes, we first forcibly reset things
    // and then make the actual switch. The reset handles things like
    // clearing active drawing artifacts and drawing instructions.
    dispatch(actionDrawResetMode());
    setTimeout(() => changeDrawMode(mode, dispatch, drawMode, false), 0);
    return;
  }

  const notDrawing =
    !mode || mode === "simple_select" || mode === "room_picker";
  const drawing = !notDrawing;

  dispatch(actionMapDrawModeUpdated({ mode }));
  dispatch(actionDrawUpdateClickCounter(mode));
  dispatch(actionDrawIsDrawingUpdated(drawing));
  // programmatically changing mode doesn't trigger draw.modechange event
  dispatch(actionMapCursorUpdated({ drawMode: drawing }));
}
